import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateShort, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Calendar } from "lucide-react";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("all");

  const fetchEvents = () => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchEvents();
    const handleReady = () => fetchEvents();
    window.addEventListener("cgc:server-ready", handleReady);
    return () => window.removeEventListener("cgc:server-ready", handleReady);
  }, []);

  const filtered = useMemo(() => events.filter((e) => {
    if (category !== "all" && e.category !== category) return false;
    if (city !== "all" && e.city !== city) return false;
    if (q && !e.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [events, q, category, city]);

  const cities = Array.from(new Set(events.map((e) => e.city)));

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="eyebrow">Directory</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">All events</h1>
        <p className="mt-3 text-stone-600 dark:text-stone-400 max-w-2xl">Search, filter and book — from fundraisers to weekend pop-ups.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="cgc-card p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
            <Input data-testid="events-search-input" placeholder="Search events…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="events-category-select" className="w-full md:w-52"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="fundraiser">Fundraiser</SelectItem>
              <SelectItem value="workshop">Workshop</SelectItem>
              <SelectItem value="popup">Pop-up</SelectItem>
              <SelectItem value="community">Community</SelectItem>
              <SelectItem value="cultural">Cultural</SelectItem>
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="events-city-select" className="w-full md:w-44"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 text-xs text-stone-500">{filtered.length} event{filtered.length !== 1 ? "s" : ""}</div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((e) => {
            const minPrice = Math.min(...(e.tiers || []).map((t) => t.price_paise));
            const link = e.organizer_slug && e.organizer_slug !== "cgc" ? `/events/${e.organizer_slug}/${e.slug}` : `/events/${e.slug}`;
            const isCollab = e.organizer_slug && e.organizer_slug !== "cgc";
            return (
              <Link key={e.id} to={link} data-testid={`event-card-${e.slug}`} className="cgc-card overflow-hidden group hover:-translate-y-0.5 transition-transform flex flex-col">
                <div className="relative w-full h-48 overflow-hidden">
                  <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 items-center">
                    <span className="bg-background/90 backdrop-blur px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider">{e.category}</span>
                    {isCollab && (
                      <span className="bg-amber-600/90 text-white font-medium px-2 py-0.5 rounded text-[10px] uppercase tracking-wider shadow-sm">
                        Collab · {e.organizer || e.organizer_slug}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-2">
                      <Calendar size={12} /> {fmtDateShort(e.start_at)} · <MapPin size={12} /> {e.city}
                    </div>
                    <h3 className="font-display text-xl font-semibold group-hover:text-[color:var(--brand-terracotta)]">{e.title}</h3>
                    {e.subtitle && <p className="text-sm text-stone-600 dark:text-stone-400 mt-2 line-clamp-2">{e.subtitle}</p>}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="font-mono">{minPrice === 0 ? "Free" : `From ${fmtINR(minPrice)}`}</span>
                    <span className="text-xs text-[color:var(--brand-terracotta)] font-medium">Book pass →</span>
                  </div>
                </div>
              </Link>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-stone-500">No events match your filters.</div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
